import { courseService } from './course.service';
import { certificationService } from './certification.service';
import { learningPathService } from './learning-path.service';
import { LtiResourceType } from '@/types/lti.types';

/** One piece of Skillzen content that an LMS link can be pointed at. */
export interface LtiContentItem {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  /** Small grey line under the title, e.g. "6 modules". */
  meta?: string;
}

export interface LoadContentParams {
  search?: string;
  limit?: number;
}

export const LTI_CONTENT_LABEL: Record<LtiResourceType, string> = {
  [LtiResourceType.COURSE]: 'course',
  [LtiResourceType.CERTIFICATION]: 'certification',
  [LtiResourceType.LEARNING_PATH]: 'learning path',
};

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Published only, for every type — a draft would 404 for the learner who opens
 * the link from their LMS weeks later.
 */
const LOADERS: Record<LtiResourceType, (params: LoadContentParams) => Promise<LtiContentItem[]>> = {
  [LtiResourceType.COURSE]: async ({ search, limit }) => {
    const result = await courseService.getMinimalCourses({
      limit,
      stage: 'published',
      ...(search ? { search } : {}),
    });
    return ((result?.courses as any[]) ?? []).map(course => ({
      id: course._id,
      title: course.title,
      description: course.description,
      image_url: course.thumbnail_image_url || course.banner_image_url,
      meta: typeof course.module_count === 'number'
        ? plural(course.module_count, 'module')
        : undefined,
    }));
  },

  [LtiResourceType.CERTIFICATION]: async ({ search, limit }) => {
    const result = await certificationService.getMinimalCertifications({
      limit,
      is_published: true,
      ...(search ? { search } : {}),
    });
    // The list is of exams; certification_id is the id a resource link maps to.
    return (result?.certifications ?? []).map(certification => ({
      id: certification.certification_id,
      title: certification.title,
      description: certification.description,
      image_url: certification.thumbnail_image_url || certification.banner_image_url,
      meta: typeof certification.question_count === 'number'
        ? `${plural(certification.question_count, 'question')} · pass ${certification.passing_score}%`
        : undefined,
    }));
  },

  [LtiResourceType.LEARNING_PATH]: async ({ search, limit }) => {
    const result = await learningPathService.listLearningPaths({
      limit,
      status: 'PUBLISHED',
      ...(search ? { search } : {}),
    });
    return (result?.items ?? []).map(path => ({
      id: path._id,
      title: path.title,
      description: path.description,
      image_url: path.thumbnail_image_url || path.banner_image_url,
      meta: typeof path.total_courses === 'number'
        ? plural(path.total_courses, 'course')
        : undefined,
    }));
  },
};

export const loadLtiContent = (
  type: LtiResourceType,
  params: LoadContentParams = {},
): Promise<LtiContentItem[]> => LOADERS[type]({ limit: 200, ...params });
